// Veo video generation service for UI helpers and configurations

export interface VeoModel {
  id: string;
  name: string;
  description: string;
  cost: number;
  duration: number;
  hasAudio: boolean;
  features: string[];
}

export interface VeoPreset {
  id: string;
  name: string;
  description: string;
  visual_style: string;
  lighting: string;
  camera_movement: string;
  audio_style: string;
  use_case: string;
}

// Veo model configurations
export const VEO_MODELS: VeoModel[] = [
  {
    id: 'veo-3.0-generate-preview',
    name: 'Veo 3',
    description: 'Premium quality with native audio',
    cost: 6.40,
    duration: 8,
    hasAudio: true,
    features: ['720p HD', 'Native Audio', 'Dialogue', 'Sound Effects', 'Ambient Noise']
  },
  {
    id: 'veo-3.0-fast-generate-preview',
    name: 'Veo 3 Fast',
    description: 'Cost-optimized with audio',
    cost: 3.20,
    duration: 8,
    hasAudio: true,
    features: ['720p HD', 'Native Audio', 'Fast Generation', 'Dialogue']
  },
  {
    id: 'veo-2.0-generate-001',
    name: 'Veo 2',
    description: 'Basic generation (silent)',
    cost: 1.60,
    duration: 8,
    hasAudio: false,
    features: ['720p HD', 'Silent Only', 'Legacy Model']
  }
];

// Veo style presets
export const VEO_STYLE_PRESETS: VeoPreset[] = [
  {
    id: 'cinematic_reveal',
    name: 'Cinematic Reveal',
    description: 'Dramatic reveal with dolly-in camera movement',
    visual_style: 'cinematic',
    lighting: 'dramatic',
    camera_movement: 'dolly_in',
    audio_style: 'cinematic',
    use_case: 'Product launches, dramatic content'
  },
  {
    id: 'product_showcase',
    name: 'Product Showcase',
    description: 'Professional product presentation',
    visual_style: 'cinematic',
    lighting: 'studio',
    camera_movement: 'smooth',
    audio_style: 'ambient',
    use_case: 'E-commerce, marketing'
  },
  {
    id: 'nature_documentary',
    name: 'Nature Documentary',
    description: 'Documentary-style with natural audio',
    visual_style: 'documentary',
    lighting: 'natural',
    camera_movement: 'pan_left',
    audio_style: 'documentary',
    use_case: 'Educational, nature content'
  },
  {
    id: 'dialogue_scene',
    name: 'Dialogue Scene',
    description: 'Character conversation with clear speech',
    visual_style: 'cinematic',
    lighting: 'natural',
    camera_movement: 'static',
    audio_style: 'dialogue_heavy',
    use_case: 'Storytelling, interviews'
  },
  {
    id: 'social_media',
    name: 'Social Media',
    description: 'Dynamic content for social platforms',
    visual_style: 'vibrant',
    lighting: 'natural',
    camera_movement: 'zoom_in',
    audio_style: 'ambient',
    use_case: 'Social media, viral content'
  },
  {
    id: 'artistic_abstract',
    name: 'Artistic Abstract',
    description: 'Creative experimental visuals',
    visual_style: 'artistic',
    lighting: 'dramatic',
    camera_movement: 'tracking',
    audio_style: 'ambient',
    use_case: 'Art projects, experimental'
  }
];

// Visual style options
export const VISUAL_STYLES = [
  { id: 'cinematic', name: 'Cinematic', description: 'Professional film-like quality' },
  { id: 'documentary', name: 'Documentary', description: 'Natural, informational style' },
  { id: 'animation', name: 'Animation', description: 'Cartoon and animated styles' },
  { id: 'film_noir', name: 'Film Noir', description: 'Dark, moody aesthetic' },
  { id: 'sci_fi', name: 'Sci-Fi', description: 'Futuristic and technological' },
  { id: 'vintage', name: 'Vintage', description: 'Retro and classic look' },
  { id: 'artistic', name: 'Artistic', description: 'Creative and experimental' }
];

// Lighting options
export const LIGHTING_OPTIONS = [
  { id: 'natural', name: 'Natural', description: 'Soft natural lighting' },
  { id: 'studio', name: 'Studio', description: 'Professional studio setup' },
  { id: 'dramatic', name: 'Dramatic', description: 'High contrast lighting' },
  { id: 'golden_hour', name: 'Golden Hour', description: 'Warm sunset lighting' },
  { id: 'blue_hour', name: 'Blue Hour', description: 'Cool twilight lighting' },
  { id: 'neon', name: 'Neon', description: 'Colorful artificial lighting' },
  { id: 'candlelight', name: 'Candlelight', description: 'Warm intimate lighting' }
];

// Camera movement options
export const CAMERA_MOVEMENTS = [
  { id: 'static', name: 'Static', description: 'No camera movement' },
  { id: 'pan_left', name: 'Pan Left', description: 'Camera pans to the left' },
  { id: 'pan_right', name: 'Pan Right', description: 'Camera pans to the right' },
  { id: 'tilt_up', name: 'Tilt Up', description: 'Camera tilts upward' },
  { id: 'tilt_down', name: 'Tilt Down', description: 'Camera tilts downward' },
  { id: 'zoom_in', name: 'Zoom In', description: 'Camera zooms into scene' },
  { id: 'zoom_out', name: 'Zoom Out', description: 'Camera zooms out' },
  { id: 'dolly_in', name: 'Dolly In', description: 'Camera moves forward' },
  { id: 'dolly_out', name: 'Dolly Out', description: 'Camera moves backward' },
  { id: 'tracking', name: 'Tracking', description: 'Camera follows subject' },
  { id: 'smooth', name: 'Smooth', description: 'Gentle fluid movement' }
];

// Audio style options (for Veo 3 models)
export const AUDIO_STYLES = [
  { id: 'cinematic', name: 'Cinematic', description: 'Epic orchestral and film-like audio' },
  { id: 'documentary', name: 'Documentary', description: 'Natural environmental sounds' },
  { id: 'dialogue_heavy', name: 'Dialogue Heavy', description: 'Clear speech and conversation' },
  { id: 'ambient', name: 'Ambient', description: 'Background atmosphere and mood' },
  { id: 'podcast', name: 'Podcast', description: 'Clear narration and speech' }
];

// Person generation options
export const PERSON_GENERATION_OPTIONS = [
  { 
    id: 'allow_adult', 
    name: 'Adults Only', 
    description: 'Generate adults only (recommended)',
    icon: '👥'
  },
  { 
    id: 'allow_all', 
    name: 'All Ages', 
    description: 'Generate people of all ages',
    icon: '👨‍👩‍👧‍👦'
  },
  { 
    id: 'dont_allow', 
    name: 'No People', 
    description: 'Do not generate any people',
    icon: '🚫'
  }
];

// Motion intensity options
export const MOTION_INTENSITY_OPTIONS = [
  { id: 'minimal', name: 'Minimal', description: 'Very subtle movement' },
  { id: 'subtle', name: 'Subtle', description: 'Gentle motion' },
  { id: 'moderate', name: 'Moderate', description: 'Balanced movement' },
  { id: 'dynamic', name: 'Dynamic', description: 'Active motion' },
  { id: 'extreme', name: 'Extreme', description: 'Intense movement' }
];

// Mood options
export const MOOD_OPTIONS = [
  { id: 'happy', name: 'Happy', description: 'Joyful and upbeat' },
  { id: 'peaceful', name: 'Peaceful', description: 'Calm and serene' },
  { id: 'dramatic', name: 'Dramatic', description: 'Intense and emotional' },
  { id: 'mysterious', name: 'Mysterious', description: 'Enigmatic and intriguing' },
  { id: 'exciting', name: 'Exciting', description: 'Energetic and thrilling' },
  { id: 'romantic', name: 'Romantic', description: 'Warm and intimate' },
  { id: 'professional', name: 'Professional', description: 'Business-like and polished' }
];

// Utility functions
export const getVeoModel = (modelId: string): VeoModel | undefined => {
  return VEO_MODELS.find(model => model.id === modelId);
};

export const getVeoPreset = (presetId: string): VeoPreset | undefined => {
  return VEO_STYLE_PRESETS.find(preset => preset.id === presetId);
};

export const formatCost = (cost: number | null | undefined): string => {
  if (cost === null || cost === undefined || typeof cost !== 'number' || isNaN(cost)) {
    return '$0.00';
  }
  return `$${cost.toFixed(2)}`;
};

export const getModelFeatureBadges = (model: VeoModel): string[] => {
  const badges = [`${model.duration}s video`];
  if (model.hasAudio) badges.push('🎵 Audio');
  if (model.cost <= 2) badges.push('💰 Budget');
  if (model.cost >= 6) badges.push('⭐ Premium');
  return badges;
};

export const buildVeoPrompt = (
  basePrompt: string,
  dialogueCue?: string,
  soundEffects?: string,
  ambientNoise?: string
): string => {
  let enhancedPrompt = basePrompt;
  
  if (dialogueCue) {
    enhancedPrompt += `. Dialogue: "${dialogueCue}"`;
  }
  
  if (soundEffects) {
    enhancedPrompt += `. Sound effects: ${soundEffects}`;
  }
  
  if (ambientNoise) {
    enhancedPrompt += `. Ambient: ${ambientNoise}`;
  }
  
  return enhancedPrompt;
};

export const getVeoAspectRatios = () => [
  { name: '16:9 (Landscape)', value: '16:9', description: 'Widescreen format' },
  { name: '9:16 (Portrait)', value: '9:16', description: 'Mobile-first format' }
];

export const getVeoDurationOptions = () => [
  { name: '8 seconds (Fixed)', value: 8, description: 'Veo generates 8-second videos' }
];

// Validation helpers
export const validateVeoSettings = (settings: any): string[] => {
  const errors: string[] = [];
  
  if (!settings.prompt || settings.prompt.trim().length === 0) {
    errors.push('Prompt is required');
  }
  
  if (settings.prompt && settings.prompt.length > 1000) {
    errors.push('Prompt must be less than 1000 characters');
  }
  
  if (settings.dialogueCue && settings.dialogueCue.length > 200) {
    errors.push('Dialogue cue must be less than 200 characters');
  }
  
  return errors;
};
